import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Gift, CheckCircle, Sparkles } from 'lucide-react';
import { useHapticFeedback } from '@/hooks/useHapticFeedback';

interface CardData {
    definition_id: number;
    name: string;
    image_filename: string;
    rarity: string;
}

interface GiftRevealModalProps {
    gifts: CardData[];
    onAccept: () => void;
}

const GiftRevealModal: React.FC<GiftRevealModalProps> = ({ gifts, onAccept }) => {
    const { impactOccurred, notificationOccurred } = useHapticFeedback();
    const [revealed, setRevealed] = useState(false);
    const [accepted, setAccepted] = useState(false);

    useEffect(() => {
        // Automatically start revealing after a short delay
        const timer = setTimeout(() => {
            setRevealed(true);
            impactOccurred('medium');
        }, 800);
        return () => clearTimeout(timer);
    }, [impactOccurred]);

    const handleAccept = () => {
        notificationOccurred('success');
        setAccepted(true);
        setTimeout(() => {
            onAccept();
        }, 500);
    };

    return (
        <AnimatePresence>
            {!accepted && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0, scale: 1.1 }}
                    transition={{ duration: 0.4 }}
                    className="fixed inset-0 z-[1000] flex flex-col items-center justify-center bg-black/90 backdrop-blur-md px-4 py-8"
                >
                    {/* Background Glow */}
                    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[150vw] h-[150vw] bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-blue-600/30 via-purple-600/10 to-transparent opacity-60 blur-3xl pointer-events-none" />

                    <motion.div
                        initial={{ y: -50, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        transition={{ delay: 0.2, type: 'spring' }}
                        className="relative z-10 flex flex-col items-center mb-8"
                    >
                        <div className="w-16 h-16 rounded-full bg-gradient-to-tr from-yellow-400 to-amber-600 flex items-center justify-center shadow-[0_0_40px_rgba(251,191,36,0.5)] mb-4">
                            <Gift className="text-white" size={32} />
                        </div>
                        <h1 className="text-3xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-amber-200 to-yellow-500 tracking-tight text-center">
                            Welcome Bonus!
                        </h1>
                        <p className="text-white/70 text-center mt-2 text-sm max-w-[280px]">
                            You have received {gifts.length} free premium cards to jumpstart your collection.
                        </p>
                    </motion.div>

                    <div className="relative w-full max-w-sm flex-1 flex flex-col justify-center min-h-[300px]">
                        <div className="flex gap-4 overflow-x-auto pb-8 snap-x snap-mandatory hide-scrollbar px-safe">
                            {gifts.map((card, idx) => (
                                <motion.div
                                    key={card.definition_id}
                                    initial={{ opacity: 0, x: 100, rotateY: 90 }}
                                    animate={revealed ? { opacity: 1, x: 0, rotateY: 0 } : {}}
                                    transition={{ 
                                        delay: revealed ? 0.3 + idx * 0.15 : 0, 
                                        type: 'spring', 
                                        damping: 15 
                                    }}
                                    className="snap-center shrink-0 w-[200px] flex flex-col items-center"
                                >
                                    <div className="relative w-full aspect-[2/3] rounded-2xl overflow-hidden border border-white/20 shadow-2xl bg-[#1c1c1e]">
                                        <div className="absolute inset-0 bg-gradient-to-tr from-white/10 to-transparent z-10 pointer-events-none" />
                                        <img 
                                            src={card.image_filename} 
                                            alt={card.name}
                                            className="w-full h-full object-cover"
                                            onError={(e) => { e.currentTarget.src = '/card_Image/default.jpg' }}
                                        />
                                        <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-black via-black/80 to-transparent z-20">
                                            <div className="text-xs font-bold text-amber-400 mb-1 drop-shadow-md">
                                                {card.rarity?.toUpperCase() || 'COMMON'}
                                            </div>
                                            <h3 className="text-white font-bold text-sm truncate leading-tight">
                                                {card.name}
                                            </h3>
                                        </div>
                                    </div>
                                </motion.div>
                            ))}
                        </div>
                    </div>

                    <motion.div
                        initial={{ opacity: 0, y: 50 }}
                        animate={revealed ? { opacity: 1, y: 0 } : {}}
                        transition={{ delay: 1.2, type: 'spring' }}
                        className="w-full max-w-sm relative z-10 mt-auto pt-6"
                    >
                        <button
                            onClick={handleAccept}
                            className="relative w-full h-[56px] rounded-2xl bg-gradient-to-r from-blue-600 to-purple-600 font-bold text-white text-lg shadow-[0_0_30px_rgba(59,130,246,0.4)] overflow-hidden group active:scale-95 transition-transform"
                        >
                            <div className="absolute inset-0 bg-white/20 translate-y-full group-active:translate-y-0 transition-transform" />
                            <div className="relative flex items-center justify-center gap-2">
                                <Sparkles size={20} />
                                <span>Accept Gifts</span>
                            </div>
                        </button>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

export default GiftRevealModal;
